import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { LocationStatus } from "@/components/LocationStatus";
import { CountdownTimer } from "@/components/CountdownTimer";
import { PrayerTimesCard } from "@/components/PrayerTimesCard";
import { useGeolocation } from "@/hooks/useGeolocation";
import { usePrayerTimes } from "@/hooks/usePrayerTimes";
import { useNotifications } from "@/hooks/useNotifications";
import { usePersistedState } from "@/hooks/usePersistedState";
import { suggestMethod } from "@/lib/geo-to-method";
import { registerServiceWorker } from "@/register-sw";
import { Toaster } from "@/components/ui/sonner";
import type { City } from "@/lib/cities";

export default function App() {
  const { coords, status: geoStatus, error: geoError, refresh, updateCoords } = useGeolocation();
  const [method, setMethod] = usePersistedState("miqat:method", "mwl");
  const [asrMethod, setAsrMethod] = usePersistedState("miqat:asr", "shafii");
  const [notifications, setNotifications] = usePersistedState("miqat:notifications", false);
  const [hasAutoSelected, setHasAutoSelected] = usePersistedState(
    "miqat:autoSelected",
    false
  );

  const [today] = useState(() => new Date());
  const { times, nextPrayer, loading } = usePrayerTimes(
    coords,
    method,
    asrMethod,
    today
  );

  const handleCitySelect = useCallback(
    (city: City) => {
      updateCoords({ latitude: city.lat, longitude: city.lng, name: `${city.name}, ${city.country}` });
      const suggested = suggestMethod(city.lat, city.lng);
      setMethod(suggested);
    },
    [updateCoords, setMethod]
  );

  const [shouldSuggest, setShouldSuggest] = useState(!hasAutoSelected);

  const handleRefreshLocation = useCallback(() => {
    setShouldSuggest(true);
    refresh();
  }, [refresh]);

  // Auto-suggest method when coords change and suggestion is pending
  useEffect(() => {
    if (coords && shouldSuggest) {
      const suggested = suggestMethod(coords.latitude, coords.longitude);
      setMethod(suggested);
      setShouldSuggest(false);
      setHasAutoSelected(true);
    }
  }, [coords, shouldSuggest, setMethod, setHasAutoSelected]);

  useNotifications(notifications, nextPrayer);

  // Register service worker and show toast when an update is available
  useEffect(() => {
    registerServiceWorker((registration) => {
      toast("New version available", {
        duration: Infinity,
        action: {
          label: "Reload",
          onClick: () => {
            registration.waiting?.postMessage({ type: "SKIP_WAITING" });
          },
        },
      });
    });
  }, []);

  return (
    <ThemeProvider defaultTheme="system">
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <Header
          asrMethod={asrMethod}
          onAsrMethodChange={setAsrMethod}
          method={method}
          onMethodChange={setMethod}
          coords={coords}
          onRefreshLocation={handleRefreshLocation}
          onCitySelect={handleCitySelect}
          notifications={notifications}
          onNotificationsChange={setNotifications}
        />
        <main className="container max-w-md mx-auto px-4 py-6 space-y-6 flex-1 overflow-y-auto">
          <LocationStatus
            coords={coords}
            status={geoStatus}
            error={geoError}
          />
          <CountdownTimer nextPrayer={nextPrayer} loading={loading} />
          <PrayerTimesCard
            times={times}
            nextPrayer={nextPrayer?.name ?? null}
            loading={loading}
          />
        </main>
        <Footer />
        <Toaster />
      </div>
    </ThemeProvider>
  );
}
